import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTenancy } from '@/contexts/TenancyContext.js';
import { toast } from 'sonner';
import { deiterateAPI } from '../../../../js/external-js/variableModules/utils/deiterateAPI.util.js';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

type TokenStatus = {
  exists: boolean;
  ptoken?: string;
  path?: string;
  record?: {
    version: string;
    tokenPart: string;
    tokenHash: string;
    name?: string;
    createdAt?: any;
    createdBy?: string;
    lastUsedAt?: any;
    revokedAt?: any;
  };
};

export default function CalendarSettingsSection() {
  const { activeTenancy } = useTenancy();
  const [loading, setLoading] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);
  const [revoking, setRevoking] = React.useState(false);
  const [status, setStatus] = React.useState<TokenStatus | null>(null);
  const [fullUrl, setFullUrl] = React.useState<string>('');
  const [outlookHelpOpen, setOutlookHelpOpen] = React.useState(false);
  const [googleHelpOpen, setGoogleHelpOpen] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  const getApiBaseUrl = (): string => {
    try {
      const w = (window as any);
      if (w && w.apiURL) return w.apiURL as string;
    } catch {}
    return (import.meta as any).env.VITE_cloudFunctionURL + '/deiterateInternalAPI';
  };

  const buildFullIcsUrl = React.useCallback((path: string): string => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = getApiBaseUrl().replace(/\/$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }, []);

  const refreshStatus = React.useCallback(async () => {
    if (!activeTenancy?.id) return;
    setLoading(true);
    try {
      const res: any = await deiterateAPI.GET('/calendar/ics/compliance/token/status', {
        companyID: activeTenancy.id,
      });
      setStatus(res);
      setFullUrl(res?.path ? buildFullIcsUrl(res.path) : '');
    } catch (e) {
      toast.error('Failed to load calendar token status');
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, buildFullIcsUrl]);

  const createOrGetToken = async () => {
    if (!activeTenancy?.id) return;
    setLoading(true);
    try {
      const res: any = await deiterateAPI.POST('/calendar/ics/compliance/token', {
        body: JSON.stringify({ name: 'Compliance Calendar' }),
        companyID: activeTenancy.id,
      });
      toast.success('Calendar subscription link ready');
      await refreshStatus();
    } catch (e) {
      toast.error('Failed to create token');
    } finally {
      setLoading(false);
    }
  };

  const rotateToken = async () => {
    if (!activeTenancy?.id) return;
    setRotating(true);
    try {
      await deiterateAPI.POST('/calendar/ics/compliance/token/rotate', {
        body: JSON.stringify({ name: 'Compliance Calendar' }),
        companyID: activeTenancy.id,
      });
      toast.success('Token rotated');
      await refreshStatus();
    } catch (e) {
      toast.error('Failed to rotate token');
    } finally {
      setRotating(false);
    }
  };

  const revokeToken = async () => {
    if (!activeTenancy?.id) return;
    setRevoking(true);
    try {
      await deiterateAPI.POST('/calendar/ics/compliance/token/revoke', {
        companyID: activeTenancy.id,
      });
      toast.success('Token revoked');
      await refreshStatus();
    } catch (e) {
      toast.error('Failed to revoke token');
    } finally {
      setRevoking(false);
    }
  };

  const copyUrl = async () => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Subscription URL copied');
    } catch {
      toast.success(`Subscription URL: ${fullUrl}`);
    }
  };

  const testFetch = async () => {
    const url = await ensureUrl();
    if (!url) return;
    setTesting(true);
    try {
      // Fire-and-forget request; response may be opaque, but server will update lastUsedAt
      void fetch(url, { mode: 'no-cors' });
      toast.success('Requested ICS fetch');
    } catch {
      toast.error('ICS fetch error');
    } finally {
      setTesting(false);
      try {
        await new Promise(r => setTimeout(r, 1500));
        await refreshStatus();
      } catch {}
    }
  };

  const ensureUrl = async (): Promise<string | null> => {
    if (fullUrl) return fullUrl;
    await createOrGetToken();
    await refreshStatus();
    return fullUrl || null;
  };

  const openInGoogleCalendar = async () => {
    setGoogleHelpOpen(true);
  };

  const openInOutlook365 = async () => {
    setOutlookHelpOpen(true);
  };

  const openInAppleCalendar = async () => {
    const url = await ensureUrl();
    if (!url) return;
    const webcalUrl = url.replace(/^https?:\/\//i, 'webcal://');
    window.open(webcalUrl, '_blank');
  };

  const confirmOutlook365 = async () => {
    const url = await ensureUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Subscription URL copied to clipboard');
    } catch {
      toast.success(`Subscription URL: ${url}`);
    }
    try {
      void fetch(url, { mode: 'no-cors' });
      setTimeout(() => { try { void refreshStatus(); } catch {} }, 1500);
    } catch {}
    window.open('https://outlook.office.com/calendar/addcalendar', '_blank', 'noopener');
    setOutlookHelpOpen(false);
  };

  const confirmGoogleCalendar = async () => {
    const url = await ensureUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Subscription URL copied to clipboard');
    } catch {
      toast.success(`Subscription URL: ${url}`);
    }
    try {
      void fetch(url, { mode: 'no-cors' });
      setTimeout(() => { try { void refreshStatus(); } catch {} }, 1500);
    } catch {}
    window.open('https://calendar.google.com/calendar/u/0/r/settings/addbyurl', '_blank', 'noopener');
    setGoogleHelpOpen(false);
  };

  React.useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const hasToken = !!status?.exists && !!fullUrl;

  const toMs = (v: any, m?: number): number | undefined => {
    if (v && typeof v.toMillis === 'function') return v.toMillis();
    if (v && typeof v._seconds === 'number') return v._seconds * 1000;
    if (v && typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof m === 'number') return m;
    return undefined;
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <h2 className="text-xl font-semibold mb-2 text-foreground">Compliance Calendar</h2>
        <p className="text-sm text-muted-foreground mb-6">Manage the tenancy's persistent calendar subscription token.</p>

        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm text-muted-foreground">Subscription URL</label>
            <div className="flex gap-2 items-center">
              <Input value={fullUrl} readOnly className="flex-1" placeholder="No token generated yet" />
              <Button variant="secondary" onClick={copyUrl} disabled={!hasToken}>Copy</Button>
              <Button variant="secondary" onClick={testFetch} disabled={!hasToken || testing}>{testing ? 'Testing...' : 'Test Fetch'}</Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <Button onClick={createOrGetToken} disabled={loading}>{loading ? 'Working...' : (hasToken ? 'Get Link' : 'Create Link')}</Button>
            <Button onClick={rotateToken} variant="outline" disabled={!status?.exists || rotating}>{rotating ? 'Rotating...' : 'Rotate'}</Button>
            <Button onClick={revokeToken} variant="destructive" disabled={!status?.exists || revoking}>{revoking ? 'Revoking...' : 'Revoke'}</Button>
          </div>

          <div className="mt-4">
            <label className="text-sm text-muted-foreground">Open in</label>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button variant="secondary" onClick={openInGoogleCalendar}>Google Calendar</Button>
              <Button variant="secondary" onClick={openInOutlook365}>Outlook 365</Button>
              <Button variant="secondary" onClick={openInAppleCalendar}>Apple Calendar</Button>
            </div>
          </div>

          {status?.record && (
            <div className="text-xs text-muted-foreground mt-2 space-y-1">
              <div>Name: {status.record.name || 'Compliance Calendar'}</div>
              <div>
                Created: {(() => { const ms = toMs(status.record.createdAt, (status as any)?.recordMillis?.createdAt); return ms ? new Date(ms).toLocaleString() : '-'; })()}
              </div>
              <div>
                Last used: {(() => { const ms = toMs(status.record.lastUsedAt, (status as any)?.recordMillis?.lastUsedAt); return ms ? new Date(ms).toLocaleString() : '-'; })()}
              </div>
              <div>
                Revoked: {(() => { const ms = toMs(status.record.revokedAt, (status as any)?.recordMillis?.revokedAt); return ms ? new Date(ms).toLocaleString() : '-'; })()}
              </div>
              <div className="mt-1">Note: "Last used" updates when a calendar client fetches the ICS feed. If it shows "-", open the subscription link or add the calendar to trigger a fetch, then refresh.</div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={outlookHelpOpen} onOpenChange={setOutlookHelpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Subscribe in Outlook 365</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-left">
                <p>We will open Outlook’s Add calendar page. Then:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Click <strong>Subscribe from web</strong>.</li>
                  <li>Paste the subscription URL we copy to your clipboard.</li>
                  <li>Give it a name (e.g., Compliance Calendar) and save.</li>
                </ol>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOutlook365}>I’ve read it – Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={googleHelpOpen} onOpenChange={setGoogleHelpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add to Google Calendar</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-left">
                <p>We will open Google Calendar’s Add by URL page. Then:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Paste the subscription URL we copy to your clipboard.</li>
                  <li>Click <strong>Add calendar</strong>.</li>
                </ol>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmGoogleCalendar}>I’ve read it – Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
